// AiOpsCoach Communications Engine - API Service Layer
import {
  mockData,
  simulateApiCall,
  generateId,
  delay
} from './api_mockdata';
import type {
  APIResponse,
  User,
  LoginCredentials,
  Component,
  Draft,
  Campaign,
  Brokerage,
  AnalyticsDashboard,
  ConversionFunnel,
  ABTestResults,
  ComponentFilters,
  BrokerageFilters,
  CampaignFilters,
  PaginationParams,
  CampaignOptions,
  SendOptions,
  PerformanceMetric,
  RealTimeMetrics,
  UserPermission,
  Role,
  CreateUserRequest,
  UpdateUserRequest,
  ChangePasswordRequest,
  CreateRoleRequest,
  UpdateRoleRequest
} from '../types';

// Export base configuration for other services
export const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

export const getAuthHeaders = (): Record<string, string> => {
  const token = localStorage.getItem('auth_token');
  return {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` })
  };
};

export const handleResponse = async (response: Response): Promise<any> => {
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  return response.json();
};

// Base API Client Class
class APIClient {
  private baseURL: string;
  private token: string | null = null;

  constructor(baseURL: string = API_BASE_URL) {
    this.baseURL = baseURL;
    this.setupToken();
  }

  private setupToken() {
    const token = localStorage.getItem('auth_token');
    if (token) {
      this.token = token;
    }
  }

  setToken(token: string) {
    this.token = token;
    localStorage.setItem('auth_token', token);
  }

  clearToken() {
    this.token = null;
    localStorage.removeItem('auth_token');
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<APIResponse<T>> {
    try {
      // In a real app, this would be a fetch call
      // For now, we'll simulate API calls with mock data
      await delay(300 + Math.random() * 500); // Random delay 300-800ms
      
      // Simulate network errors occasionally
      if (Math.random() < 0.02) { // 2% chance
        throw new Error('Network error: Unable to connect to server');
      }
      
      return {
        success: true,
        data: {} as T, // Will be overridden in specific methods
        metadata: {
          timestamp: new Date().toISOString(),
          requestId: generateId()
        }
      };
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Unknown API error');
    }
  }
}

// Authentication Service
export class AuthService {
  private client: APIClient;

  constructor() {
    this.client = new APIClient();
  }

  async login(credentials: LoginCredentials): Promise<APIResponse<{ user: User; token: string }>> {
    try {
      // Try backend API first
      const response = await fetch(`${API_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(credentials),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Invalid credentials');
      }

      const data = await response.json();
      
      // Store tokens
      if (data.access_token) {
        this.client.setToken(data.access_token);
        localStorage.setItem('refresh_token', data.refresh_token || '');
      }

      // Transform response to match expected format
      return {
        success: true,
        data: {
          user: data.user,
          token: data.access_token
        },
        message: data.message || 'Login successful'
      };
    } catch (error) {
      console.log('Backend API unavailable, using mock data for development...');
      
      // Fallback to mock data for development
      const { mockUsers } = await import('./api_mockdata');
      const user = mockUsers.find(u => u.email === credentials.email);
      
      if (!user) {
        throw new Error('Invalid credentials');
      }
      
      // Simulate successful login with mock user
      const token = user.accessToken || 'mock-jwt-token';
      this.client.setToken(token);
      
      console.log('Using mock user login for:', user.email, 'with roles:', user.roles);
      
      return {
        success: true,
        data: {
          user,
          token
        },
        message: 'Login successful (mock)'
      };
    }
  }

  async logout(): Promise<APIResponse<void>> {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/logout`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });

      this.client.clearToken();
      localStorage.removeItem('refresh_token');
      
      return {
        success: true,
        data: undefined,
        message: 'Logout successful'
      };
    } catch (error) {
      throw error;
    }
  }

  async refreshToken(): Promise<APIResponse<{ token: string }>> {
    try {
      const refreshToken = localStorage.getItem('refresh_token');
      const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });

      if (!response.ok) {
        throw new Error('Failed to refresh token');
      }

      const data = await response.json();
      
      if (data.access_token) {
        this.client.setToken(data.access_token);
      }
      
      return {
        success: true,
        data: { token: data.access_token },
        message: 'Token refreshed'
      };
    } catch (error) {
      throw error;
    }
  }

  async getCurrentUser(): Promise<APIResponse<User>> {
    try {
      // Try backend API first
      const response = await fetch(`${API_BASE_URL}/auth/profile`, {
        method: 'GET',
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        // Check for authentication/authorization failures
        if (response.status === 401 || response.status === 403) {
          // Don't fall back to mock data for auth failures - propagate the error
          throw new Error('Authentication failed - token invalid or expired');
        }
        // For other HTTP errors, also throw (don't fallback)
        throw new Error(`Failed to get user profile: ${response.status}`);
      }

      const data = await response.json();
      
      return {
        success: true,
        data: data.user,
        message: 'User profile retrieved'
      };
    } catch (error) {
      // Only fall back to mock data for network connectivity issues, not auth failures
      if (error instanceof TypeError && error.message.includes('fetch')) {
        console.log('Backend API unavailable, using mock data for current user...');
        
        // Fallback to mock data for development
        const { mockUsers } = await import('./api_mockdata');
        const token = localStorage.getItem('auth_token');
        
        // Find user by token (this is a simplified approach for mock data)
        const user = mockUsers.find(u => u.accessToken === token) || mockUsers[0]; // Default to first user
        
        if (!user) {
          throw new Error('User not found');
        }
        
        console.log('Using mock user profile for:', user.email, 'with roles:', user.roles);
        
        return {
          success: true,
          data: user,
          message: 'User profile retrieved (mock)'
        };
      } else {
        // Propagate authentication and other errors (don't use mock data)
        throw error;
      }
    }
  }

  // User Management Methods
  async getAllUsers(): Promise<APIResponse<User[]>> {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/users`, {
        method: 'GET',
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error('Failed to fetch users');
      }

      const data = await response.json();
      
      return {
        success: true,
        data: data.users || [],
        message: 'Users retrieved successfully'
      };
    } catch (error) {
      console.log('Backend API unavailable, using mock data for users...');
      
      // Fallback to mock data for development
      const { mockUsers } = await import('./api_mockdata');
      
      // Format mock users to match expected API structure
      const formattedUsers = mockUsers.map(user => ({
        ...user,
        id: user.id || user.user_id || user.email,
        user_id: user.user_id || user.id || user.email,
        status: user.status || 'active',
        roles: user.roles || (user.role ? [user.role] : ['user']),
        permissions: user.permissions || ['user', 'read']
      }));
      
      console.log('Using mock users data:', formattedUsers.length, 'users');
      
      return {
        success: true,
        data: formattedUsers,
        message: 'Users retrieved successfully (mock)'
      };
    }
  }

  async createUser(userData: CreateUserRequest): Promise<APIResponse<User>> {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/users`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(userData),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to create user');
      }

      const data = await response.json();
      
      return {
        success: true,
        data: data.user,
        message: 'User created successfully'
      };
    } catch (error) {
      throw error;
    }
  }

  async updateUser(userId: string, updates: UpdateUserRequest): Promise<APIResponse<User>> {
    try {
      const authHeaders = getAuthHeaders();
      console.log('=== USER UPDATE DEBUG ===');
      console.log('User ID:', userId);
      console.log('Update data being sent:', JSON.stringify(updates, null, 2));
      console.log('API URL:', `${API_BASE_URL}/admin/users/${userId}`);
      console.log('Auth headers:', authHeaders);
      console.log('========================');
      
      const response = await fetch(`${API_BASE_URL}/admin/users/${userId}`, {
        method: 'PUT',
        headers: authHeaders,
        body: JSON.stringify(updates),
      });

      console.log('API Response status:', response.status);
      console.log('API Response headers:', Object.fromEntries(response.headers.entries()));

      if (!response.ok) {
        // Handle specific error cases
        if (response.status === 401 || response.status === 403) {
          console.error('Authentication/Authorization failed for user update');
          return {
            success: false,
            data: null,
            message: 'Authentication failed. Please log in again and try updating the user.'
          };
        }
        
        let errorText;
        try {
          errorText = await response.text();
          console.error('API Error response body:', errorText);
          
          // Try to parse as JSON
          const error = JSON.parse(errorText);
          return {
            success: false,
            data: null,
            message: error.message || `Failed to update user: HTTP ${response.status}`
          };
        } catch (parseError) {
          console.error('Failed to parse error response:', parseError);
          return {
            success: false,
            data: null,
            message: `Failed to update user: HTTP ${response.status}. Response: ${errorText}`
          };
        }
      }

      const responseText = await response.text();
      console.log('Raw API response:', responseText);
      
      let data;
      try {
        data = JSON.parse(responseText);
        console.log('Parsed response data:', data);
      } catch (parseError) {
        console.error('Failed to parse success response:', parseError);
        return {
          success: false,
          data: null,
          message: 'Invalid response format from server'
        };
      }
      
      // Debug the response structure
      console.log('Backend response structure:');
      console.log('- data:', data);
      console.log('- data.user:', data.user);
      console.log('- data.message:', data.message);
      
      const userData = data.user || data;
      console.log('Using userData:', userData);
      
      return {
        success: true,
        data: userData,
        message: data.message || 'User updated successfully'
      };
    } catch (error) {
      console.error('Network or parsing error during user update:', error);
      
      // No mock data fallback - return the actual error
      return {
        success: false,
        data: null,
        message: error instanceof Error ? error.message : 'Failed to update user. Please check your connection and authentication.'
      };
    }
  }

  async deleteUser(userId: string): Promise<APIResponse<void>> {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/users/${userId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to delete user');
      }
      
      return {
        success: true,
        data: undefined,
        message: 'User deleted successfully'
      };
    } catch (error) {
      throw error;
    }
  }

  async changePassword(request: ChangePasswordRequest): Promise<APIResponse<void>> {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/users/${request.userId}/password`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          newPassword: request.newPassword,
          confirmPassword: request.confirmPassword
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to change password');
      }
      
      return {
        success: true,
        data: undefined,
        message: 'Password changed successfully'
      };
    } catch (error) {
      throw error;
    }
  }

  async getUserRoles(): Promise<APIResponse<Role[]>> {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/roles`, {
        method: 'GET',
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error('Failed to fetch roles');
      }

      const data = await response.json();
      
      return {
        success: true,
        data: data.roles || [],
        message: 'Roles retrieved successfully'
      };
    } catch (error) {
      console.log('Backend API unavailable, using mock data for roles...');
      
      // Fallback to mock data for development
      const mockRoles: Role[] = [
        {
          id: 'admin',
          role_id: 'admin',
          name: 'Administrator',
          description: 'Full system access with all permissions',
          permissions: ['admin', 'user', 'read', 'write', 'delete'],
          is_default: true
        },
        {
          id: 'manager',
          role_id: 'manager',
          name: 'Manager',
          description: 'Management level access with read/write permissions',
          permissions: ['user', 'read', 'write', 'projects', 'communications'],
          is_default: true
        },
        {
          id: 'user',
          role_id: 'user',
          name: 'Standard User',
          description: 'Standard user with basic permissions',
          permissions: ['user', 'read'],
          is_default: true
        }
      ];
      
      console.log('Using mock roles data:', mockRoles.length, 'roles');
      
      return {
        success: true,
        data: mockRoles,
        message: 'Roles retrieved successfully (mock)'
      };
    }
  }

  async updateUserPermissions(userId: string, permissions: UserPermission[]): Promise<APIResponse<User>> {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/users/${userId}/permissions`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ permissions }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to update permissions');
      }

      const data = await response.json();
      
      return {
        success: true,
        data: data.user,
        message: 'User permissions updated successfully'
      };
    } catch (error) {
      throw error;
    }
  }

  async getAvailablePermissions(): Promise<APIResponse<UserPermission[]>> {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/permissions`, {
        method: 'GET',
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error('Failed to fetch permissions');
      }

      const data = await response.json();
      
      return {
        success: true,
        data: data.permissions || [],
        message: 'Permissions retrieved successfully'
      };
    } catch (error) {
      console.log('Backend API unavailable, using mock data for permissions...');
      
      // Fallback to mock data for development
      const mockPermissions: UserPermission[] = [
        {
          id: 'admin',
          name: 'Administrator',
          description: 'Full administrative access to all system features',
          resource: 'admin',
          granted: false
        },
        {
          id: 'user',
          name: 'User Access',
          description: 'Basic user access to standard features',
          resource: 'user',
          granted: false
        },
        {
          id: 'read',
          name: 'Read Access',
          description: 'Permission to read and view data',
          resource: 'read',
          granted: false
        },
        {
          id: 'write',
          name: 'Write Access',
          description: 'Permission to create and update data',
          resource: 'write',
          granted: false
        },
        {
          id: 'delete',
          name: 'Delete Access',
          description: 'Permission to delete data',
          resource: 'delete',
          granted: false
        },
        {
          id: 'projects',
          name: 'Projects Access',
          description: 'Access to project management features',
          resource: 'projects',
          granted: false
        },
        {
          id: 'communications',
          name: 'Communications Access',
          description: 'Access to communication and messaging features',
          resource: 'communications',
          granted: false
        }
      ];
      
      console.log('Using mock permissions data:', mockPermissions.length, 'permissions');
      
      return {
        success: true,
        data: mockPermissions,
        message: 'Permissions retrieved successfully (mock)'
      };
    }
  }

  // Role Management Methods
  async createRole(roleData: CreateRoleRequest): Promise<APIResponse<Role>> {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/roles`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(roleData),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to create role');
      }

      const data = await response.json();
      
      return {
        success: true,
        data: data.role,
        message: 'Role created successfully'
      };
    } catch (error) {
      throw error;
    }
  }

  async updateRole(roleId: string, updates: UpdateRoleRequest): Promise<APIResponse<Role>> {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/roles/${roleId}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(updates),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to update role');
      }

      const data = await response.json();
      
      return {
        success: true,
        data: data.role,
        message: 'Role updated successfully'
      };
    } catch (error) {
      throw error;
    }
  }

  async deleteRole(roleId: string): Promise<APIResponse<void>> {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/roles/${roleId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to delete role');
      }
      
      return {
        success: true,
        data: undefined,
        message: 'Role deleted successfully'
      };
    } catch (error) {
      throw error;
    }
  }
}



export const newsletterService = {
  subscribe: async (data) => {
    const fullUrl = `${API_BASE_URL}/newsletter/signup`;
    console.log('Newsletter signup attempt:', data);
    console.log('API_BASE_URL:', API_BASE_URL);
    console.log('Full URL being called:', fullUrl);
    
    // Add detailed fetch logging
    console.log('About to make fetch request with:', {
      url: fullUrl,
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify(data)
    });
    
    try {
      console.log('Calling fetch...');
      
      // Add timeout to prevent infinite hanging
      const controller = new AbortController();
      const timeoutId = setTimeout(() => {
        console.error('Fetch timeout after 10 seconds');
        controller.abort();
      }, 10000);
      
      const response = await fetch(fullUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(data),
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      console.log('Fetch completed, got response object');
      
      console.log('Newsletter signup response:', {
        status: response.status,
        statusText: response.statusText,
        ok: response.ok,
        url: response.url,
        headers: Object.fromEntries(response.headers.entries())
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Newsletter signup failed:', {
          status: response.status,
          statusText: response.statusText,
          errorBody: errorText,
          url: response.url,
          headers: Object.fromEntries(response.headers.entries())
        });
        throw new Error(`Newsletter signup failed: ${response.status} ${response.statusText} - ${errorText}`);
      }

      const result = await response.json();
      console.log('Newsletter signup success:', result);
      return result;
    } catch (error) {
      console.error('Newsletter signup error:', error);
      if (error instanceof TypeError && error.message.includes('fetch')) {
        console.error('Network error - check if API endpoint is reachable:', `${API_BASE_URL}/newsletter/signup`);
      }
      throw error;
    }
  }
};


// Components Service
export class ComponentsService {
  private client: APIClient;

  constructor() {
    this.client = new APIClient();
  }

  async getComponents(
    filters: ComponentFilters = {},
    pagination: PaginationParams = {}
  ): Promise<APIResponse<Component[]>> {
    let components = [...mockData.hooks, ...mockData.sections, ...mockData.signatures];
    
    // Apply filters
    if (filters.type) {
      components = components.filter(c => c.type === filters.type);
    }
    
    if (filters.tags && filters.tags.length > 0) {
      components = components.filter(c => 
        c.tags.some(tag => filters.tags!.includes(tag))
      );
    }
    
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      components = components.filter(c =>
        c.content.toLowerCase().includes(searchLower) ||
        c.tags.some(tag => tag.toLowerCase().includes(searchLower))
      );
    }
    
    if (filters.performanceScore) {
      const [min, max] = filters.performanceScore;
      components = components.filter(c => 
        c.performanceScore >= min && c.performanceScore <= max
      );
    }

    // Apply sorting
    if (pagination.sortBy) {
      components.sort((a, b) => {
        const aVal = (a as any)[pagination.sortBy!];
        const bVal = (b as any)[pagination.sortBy!];
        const order = pagination.sortOrder === 'desc' ? -1 : 1;
        return aVal > bVal ? order : aVal < bVal ? -order : 0;
      });
    }

    return await simulateApiCall(components);
  }

  async getComponent(id: string): Promise<APIResponse<Component>> {
    const component = [...mockData.hooks, ...mockData.sections, ...mockData.signatures]
      .find(c => c.id === id);
    
    if (!component) {
      throw new Error(`Component not found: ${id}`);
    }
    
    return await simulateApiCall(component);
  }

  async createComponent(data: Partial<Component>): Promise<APIResponse<Component>> {
    const newComponent = {
      id: generateId(),
      content: data.content || '',
      type: data.type || 'hook',
      performanceScore: 0,
      usageCount: 0,
      tags: data.tags || [],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      ...data
    } as Component;

    return await simulateApiCall(newComponent);
  }

  async updateComponent(id: string, updates: Partial<Component>): Promise<APIResponse<Component>> {
    const existing = [...mockData.hooks, ...mockData.sections, ...mockData.signatures]
      .find(c => c.id === id);
    
    if (!existing) {
      throw new Error(`Component not found: ${id}`);
    }

    const updated = {
      ...existing,
      ...updates,
      updatedAt: new Date().toISOString()
    };

    return await simulateApiCall(updated);
  }

  async deleteComponent(id: string): Promise<APIResponse<void>> {
    const component = [...mockData.hooks, ...mockData.sections, ...mockData.signatures]
      .find(c => c.id === id);
    
    if (!component) {
      throw new Error(`Component not found: ${id}`);
    }

    return await simulateApiCall(undefined, 300);
  }
}

// Drafts Service
export class DraftsService {
  private client: APIClient;

  constructor() {
    this.client = new APIClient();
  }

  async getDrafts(filters: any = {}): Promise<APIResponse<Draft[]>> {
    let drafts = [...mockData.drafts];
    
    if (filters.status) {
      drafts = drafts.filter(d => d.status === filters.status);
    }
    
    return await simulateApiCall(drafts);
  }

  async getDraft(id: string): Promise<APIResponse<Draft>> {
    const draft = mockData.drafts.find(d => d.id === id);
    
    if (!draft) {
      throw new Error(`Draft not found: ${id}`);
    }
    
    return await simulateApiCall(draft);
  }

  async createDraft(data: Partial<Draft>): Promise<APIResponse<Draft>> {
    const newDraft = {
      id: generateId(),
      name: data.name || 'Untitled Draft',
      status: 'draft' as const,
      hook: data.hook || null,
      sections: data.sections || [],
      signature: data.signature || null,
      subjectLine: data.subjectLine || '',
      previewText: data.previewText || '',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      createdBy: 'user-001',
      tags: data.tags || [],
      ...data
    } as Draft;

    return await simulateApiCall(newDraft);
  }

  async updateDraft(id: string, updates: Partial<Draft>): Promise<APIResponse<Draft>> {
    const existing = mockData.drafts.find(d => d.id === id);
    
    if (!existing) {
      throw new Error(`Draft not found: ${id}`);
    }

    const updated = {
      ...existing,
      ...updates,
      updatedAt: new Date().toISOString()
    };

    return await simulateApiCall(updated);
  }

  async deleteDraft(id: string): Promise<APIResponse<void>> {
    const draft = mockData.drafts.find(d => d.id === id);
    
    if (!draft) {
      throw new Error(`Draft not found: ${id}`);
    }

    return await simulateApiCall(undefined, 400);
  }

  async duplicateDraft(id: string): Promise<APIResponse<Draft>> {
    const original = mockData.drafts.find(d => d.id === id);
    
    if (!original) {
      throw new Error(`Draft not found: ${id}`);
    }

    const duplicated = {
      ...original,
      id: generateId(),
      name: `${original.name} (Copy)`,
      status: 'draft' as const,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    return await simulateApiCall(duplicated, 600);
  }
}

// Brokerages Service
export class BrokeragesService {
  private client: APIClient;

  constructor() {
    this.client = new APIClient();
  }

  async getBrokerages(
    filters: BrokerageFilters = {},
    pagination: PaginationParams = {}
  ): Promise<APIResponse<Brokerage[]>> {
    let brokerages = [...mockData.brokerages];
    
    // Apply filters
    if (filters.vertical && filters.vertical.length > 0) {
      brokerages = brokerages.filter(b => filters.vertical!.includes(b.vertical));
    }
    
    if (filters.states && filters.states.length > 0) {
      brokerages = brokerages.filter(b => filters.states!.includes(b.state));
    }
    
    if (filters.agentCountRange) {
      const [min, max] = filters.agentCountRange;
      brokerages = brokerages.filter(b => b.agentCount >= min && b.agentCount <= max);
    }
    
    if (filters.revenueRange) {
      const [min, max] = filters.revenueRange;
      brokerages = brokerages.filter(b => b.revenue >= min && b.revenue <= max);
    }
    
    return await simulateApiCall(brokerages);
  }

  async getBrokerage(id: string): Promise<APIResponse<Brokerage>> {
    const brokerage = mockData.brokerages.find(b => b.id === id);
    
    if (!brokerage) {
      throw new Error(`Brokerage not found: ${id}`);
    }
    
    return await simulateApiCall(brokerage);
  }
}

// Campaigns Service
export class CampaignsService {
  private client: APIClient;

  constructor() {
    this.client = new APIClient();
  }

  async getCampaigns(filters: CampaignFilters = {}): Promise<APIResponse<Campaign[]>> {
    let campaigns = [...mockData.campaigns];
    
    if (filters.status && filters.status.length > 0) {
      campaigns = campaigns.filter(c => filters.status!.includes(c.status));
    }
    
    return await simulateApiCall(campaigns);
  }

  async getCampaign(id: string): Promise<APIResponse<Campaign>> {
    const campaign = mockData.campaigns.find(c => c.id === id);
    
    if (!campaign) {
      throw new Error(`Campaign not found: ${id}`);
    }
    
    return await simulateApiCall(campaign);
  }

  async createCampaign(draftId: string, options: CampaignOptions): Promise<APIResponse<Campaign>> {
    const draft = mockData.drafts.find(d => d.id === draftId);
    
    if (!draft) {
      throw new Error(`Draft not found: ${draftId}`);
    }

    const newCampaign: Campaign = {
      id: generateId(),
      name: options.name,
      status: options.scheduledDate ? 'scheduled' : 'active',
      draftId,
      recipients: options.recipients.length,
      sentDate: options.scheduledDate ? undefined : new Date().toISOString(),
      scheduledDate: options.scheduledDate,
      performance: {
        sent: 0,
        delivered: 0,
        opened: 0,
        clicked: 0,
        converted: 0,
        bounced: 0,
        unsubscribed: 0,
        openRate: 0,
        clickRate: 0,
        conversionRate: 0,
        bounceRate: 0
      },
      segments: options.segments,
      abTest: options.abTest ? {
        isActive: true,
        variants: [],
        winner: null
      } : undefined
    };

    return await simulateApiCall(newCampaign, 800);
  }

  async sendCampaign(campaignId: string, options: SendOptions = { scheduleType: 'immediate' }): Promise<APIResponse<void>> {
    const campaign = mockData.campaigns.find(c => c.id === campaignId);
    
    if (!campaign) {
      throw new Error(`Campaign not found: ${campaignId}`);
    }

    // Simulate sending process
    return await simulateApiCall(undefined, 1000);
  }

  async pauseCampaign(campaignId: string): Promise<APIResponse<void>> {
    return await simulateApiCall(undefined, 300);
  }

  async resumeCampaign(campaignId: string): Promise<APIResponse<void>> {
    return await simulateApiCall(undefined, 300);
  }
}

// Analytics Service
export class AnalyticsService {
  private client: APIClient;

  constructor() {
    this.client = new APIClient();
  }

  async getDashboardData(timeRange: string = '7d'): Promise<APIResponse<AnalyticsDashboard>> {
    const dashboard = {
      ...mockData.analytics.dashboard,
      timeRange
    };
    
    return await simulateApiCall(dashboard, 600);
  }

  async getConversionFunnel(campaignId: string): Promise<APIResponse<ConversionFunnel>> {
    return await simulateApiCall(mockData.analytics.conversionFunnel);
  }

  async getPerformanceMetrics(
    timeRange: string = '7d',
    campaignId?: string
  ): Promise<APIResponse<PerformanceMetric[]>> {
    return await simulateApiCall(mockData.analytics.performanceMetrics);
  }

  async getABTestResults(campaignId: string): Promise<APIResponse<ABTestResults>> {
    return await simulateApiCall(mockData.abTestResults);
  }

  async getRealTimeMetrics(): Promise<APIResponse<RealTimeMetrics>> {
    // Simulate real-time updates by modifying data slightly
    const metrics = {
      ...mockData.realTimeMetrics,
      totalSent: mockData.realTimeMetrics.totalSent + Math.floor(Math.random() * 10),
      avgOpenRate: mockData.realTimeMetrics.avgOpenRate + (Math.random() - 0.5) * 2,
      totalConversions: mockData.realTimeMetrics.totalConversions + Math.floor(Math.random() * 3)
    };
    
    return await simulateApiCall(metrics, 100);
  }
}

// Export service instances
export const authService = new AuthService();
export const componentsService = new ComponentsService();
export const draftsService = new DraftsService();
export const brokeragesService = new BrokeragesService();
export const campaignsService = new CampaignsService();
export const analyticsService = new AnalyticsService();

// Export all services as a single object
export const apiServices = {
  auth: authService,
  components: componentsService,
  drafts: draftsService,
  brokerages: brokeragesService,
  campaigns: campaignsService,
  analytics: analyticsService
};

// WebSocket Mock for Real-time Updates
export class WebSocketService {
  private callbacks: Map<string, ((data: any) => void)[]> = new Map();
  private intervals: Map<string, NodeJS.Timeout> = new Map();

  connect(url: string): void {
    console.log(`Mock WebSocket connected to ${url}`);
    
    // Simulate periodic updates
    const interval = setInterval(() => {
      this.simulateUpdate();
    }, 5000);
    
    this.intervals.set(url, interval);
  }

  subscribe(channel: string, callback: (data: any) => void): void {
    if (!this.callbacks.has(channel)) {
      this.callbacks.set(channel, []);
    }
    this.callbacks.get(channel)!.push(callback);
  }

  unsubscribe(channel: string): void {
    this.callbacks.delete(channel);
  }

  disconnect(): void {
    this.intervals.forEach(interval => clearInterval(interval));
    this.intervals.clear();
    this.callbacks.clear();
  }

  private simulateUpdate(): void {
    const updates = [
      {
        channel: 'analytics_updates',
        data: {
          type: 'email_opened',
          campaignId: 'campaign-002',
          timestamp: new Date().toISOString(),
          data: { recipient: 'Elite Realty Partners' }
        }
      },
      {
        channel: 'campaign_updates',
        data: {
          type: 'conversion',
          campaignId: 'campaign-001',
          timestamp: new Date().toISOString(),
          data: { recipient: 'Mountain View Realty', value: '$12,500' }
        }
      }
    ];

    const randomUpdate = updates[Math.floor(Math.random() * updates.length)];
    const callbacks = this.callbacks.get(randomUpdate.channel);
    
    if (callbacks) {
      callbacks.forEach(callback => callback(randomUpdate.data));
    }
  }
}

export const webSocketService = new WebSocketService();

export default apiServices;